import { Plus } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { QueryError } from '@/components/QueryError';
import { CustomRoleEditor } from '@/components/team/CustomRoleEditor';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { apiErrorMessage } from '@/lib/clients';
import {
  blockingMembers, permissionLabel, useCustomRoles, useDeleteCustomRole, type BlockingMember, type CustomRole,
} from '@/lib/custom-roles';
import { memberName, useUsersByIds } from '@/lib/users';

/** Rôles personnalisés du tenant : création, édition, suppression (onglet de /team, users.changeRole). */
export function CustomRolesList() {
  const query = useCustomRoles();
  const remove = useDeleteCustomRole();
  const [editor, setEditor] = useState<{ role: CustomRole | null } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CustomRole | null>(null);
  const [blocking, setBlocking] = useState<BlockingMember[] | null>(null);

  const onDelete = () => {
    if (!deleteTarget) return;
    remove.mutate(deleteTarget.id, {
      onSuccess: (res) => {
        toast.success(res.affectedMemberships > 0
          ? `Rôle supprimé. ${res.affectedMemberships} membres sont retombés sur leur rôle built-in.`
          : `Rôle « ${deleteTarget.name} » supprimé`);
        setDeleteTarget(null);
      },
      onError: (err) => {
        const list = blockingMembers(err);
        setDeleteTarget(null);
        if (list.length) setBlocking(list);
        else toast.error(apiErrorMessage(err, 'Erreur lors de la suppression'));
      },
    });
  };

  const { data, isLoading } = query;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setEditor({ role: null })}><Plus className="h-4 w-4 mr-2" /> Nouveau rôle</Button>
      </div>
      <QueryError query={query} />
      <div className="bg-card rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nom</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="text-center">Permissions</TableHead>
              <TableHead className="text-center">Membres</TableHead>
              <TableHead className="text-right"><span className="sr-only">Actions</span></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && Array.from({ length: 3 }).map((_, i) => (
              <TableRow key={i}>{Array.from({ length: 5 }).map((__, j) => <TableCell key={j}><Skeleton className="h-4" /></TableCell>)}</TableRow>
            ))}
            {data?.data.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell className="text-muted-foreground max-w-xs truncate">{r.description || '—'}</TableCell>
                <TableCell className="text-center">{r.permissions.length}</TableCell>
                <TableCell className="text-center">{r.membersCount ?? 0}</TableCell>
                <TableCell className="text-right space-x-2">
                  <Button variant="outline" size="sm" onClick={() => setEditor({ role: r })}>Éditer</Button>
                  <Button variant="ghost" size="sm" className="text-danger" onClick={() => setDeleteTarget(r)}>Supprimer</Button>
                </TableCell>
              </TableRow>
            ))}
            {data && data.data.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Aucun rôle personnalisé</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <CustomRoleEditor open={!!editor} role={editor?.role ?? null} onOpenChange={(o) => { if (!o) setEditor(null); }} />

      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Supprimer le rôle</DialogTitle>
            <DialogDescription>
              {deleteTarget?.membersCount
                ? `${deleteTarget.membersCount} membre(s) portent « ${deleteTarget.name} » : ils retomberont sur leur rôle standard.`
                : `Le rôle « ${deleteTarget?.name ?? ''} » sera définitivement supprimé.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Annuler</Button>
            <Button variant="destructive" disabled={remove.isPending} onClick={onDelete}>
              {remove.isPending ? 'Suppression…' : 'Supprimer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BlockingDialog blocking={blocking} onClose={() => setBlocking(null)} />
    </div>
  );
}

/** Refus de suppression (403) : membres qui gagneraient des permissions que l'acteur n'a pas. Pas de retry. */
function BlockingDialog({ blocking, onClose }: { blocking: BlockingMember[] | null; onClose: () => void }) {
  // La réponse ne porte que les ids : noms résolus en un appel par userId
  const { data: users } = useUsersByIds(blocking?.map((b) => b.userId) ?? [], !!blocking);
  const name = (b: BlockingMember) => {
    const m = users?.get(b.userId);
    return m ? memberName(m) : `Membre ${b.userId.slice(-4)}`;
  };

  return (
    <Dialog open={!!blocking} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Suppression impossible</DialogTitle>
          <DialogDescription>
            Cette suppression accorderait des permissions que vous n&apos;avez pas aux membres suivants :
          </DialogDescription>
        </DialogHeader>
        <ul className="space-y-2 text-sm" aria-label="Membres bloquants">
          {blocking?.map((b) => (
            <li key={b.membershipId}>
              <span className="font-medium">{name(b)}</span>
              <span className="text-muted-foreground"> — gagnerait : {b.missing.map(permissionLabel).join(', ')}</span>
            </li>
          ))}
        </ul>
        <DialogFooter>
          <Button onClick={onClose}>Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
